import React, { useContext, useCallback, useState, useEffect } from 'react'
import { styled } from 'linaria/react'
import { fetchProductReviews } from '../../TestFreaks/Api'
import { ProductAccordion } from './ProductAccordion'
import {
   CheckCircle,
   NavArrowRight,
   Prohibition,
   Star,
   ThumbsDown,
   ThumbsUp,
} from 'iconoir-react'
import StarIcon from '../../Core/Icons/StarIcon'
import { css } from 'linaria'
import { SiteContext } from '../../Global/SiteContext'
import { calculateDaysSinceDate, getHostname } from '../../Core/Util/Helpers'
import { theme } from '../../Theme'
import SiteButton from '../../Core/SiteButton'
import { useIntl } from '@jetshop/intl'
import ChannelContext from '@jetshop/core/components/ChannelContext/ChannelContext'

const GradeText = css`
   font-size: 10px;
   font-weight: 600;
`

const ScoreText = css`
   font-family: var(--font-grotesk);
   font-size: 48px;
`

const ReviewsContainer = styled.div`
   .review-actions {
      display: flex;
      align-items: center;
      button {
         background: transparent;
      }

      button:hover {
         transition: opacity 0.25s ease;
         opacity: 0.5;
      }
   }

   .user-avatar {
      width: 100px;
      height: 100px;
      background: #195178;
      border-radius: 50%;
      color: #fff;
      font-size: 2rem;
      font-weight: 700;
      text-align: center;
      aspect-ratio: 1/1;
      position: relative;
      display: flex;
      justify-content: center;
      align-content: center;
      flex-wrap: wrap;

      ${theme.below.lg} {
         width: 50px;
         height: 50px;
      }
   }

   .user-verified svg {
      position: absolute;
      width: 2rem;
      height: 2rem;
      right: -0.25rem;
      bottom: -0.25rem;
      padding: 2px;
      background: #1d9bf0;
      border-radius: 50%;
      border: 4px solid #fff;
   }

   ${theme.above.xxl} {
      .col-xxl-11 {
         width: 88%;
      }
      .col-xxl-1 {
         width: 12%;
      }
   }

   // Overflow hidden-wrapper on review-images, preserve same width and height on all images
   .review-img-gallery > .review-img-wrapper {
      width: 80px;
      height: 80px;
      overflow: hidden;
      position: relative;
   }

   .review-img-gallery img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
   }
`

const ProgressBarContainer = styled.div`
   .progress {
      background-color: transparent;
      border: 1px solid black;
      margin-bottom: 0.5rem;
      padding: 2px;
      height: 0.75rem;
      width: 100%;
   }

   .progress-bar {
      background-color: var(--color-vb-blue);
      border-radius: var(--bs-border-radius);
   }

   .progress:last-child {
      margin-bottom: 0rem;
   }
`

const ReviewsStats = styled.div`
   min-width: 100px;
`

const ProductReviews = () => {
   const { activeVariant, productReviews, reviewsAccOpen } =
      useContext(SiteContext)
   const selectedChannel = useContext(ChannelContext)?.selectedChannel
   const summary = productReviews?.summary
   const [reviews, setReviews] = useState(null)
   const [page, setPage] = useState(1)
   const [hasNextPage, setHasNextPage] = useState(false)
   const t = useIntl()

   //Init hook
   useEffect(() => {
      setReviews(productReviews?.reviews)
      setHasNextPage(
         productReviews?.summary?.your_review_count >
            productReviews?.reviews?.length
      )
   }, [productReviews])

   const loadMoreReviews = async () => {
      const response = await fetchProductReviews(
         activeVariant?.articleNumber,
         10,
         page + 1,
         getHostname(selectedChannel?.url, 'verktygsboden.se')
      )
      const updatedReviews = [...reviews, ...response?.reviews]
      setReviews(updatedReviews)
      setHasNextPage(
         response?.summary?.your_review_count > updatedReviews.length
      )
      setPage(prev => prev + 1)
   }

   return (
      <section id="product-review">
         <ProductAccordion
            title={`${t('reviews')}`}
            subTitle={`(${summary?.your_review_count ?? 0} st)`}
            icon={
               <Star width="1.8rem" height="1.8rem" strokeWidth={2.5}></Star>
            }
            isOpen={reviewsAccOpen}
         >
            <div className="row m-0 p-0">
               <ReviewsStats className="col-3 col-xl-2 d-flex flex-column align-items-center justify-content-center">
                  <h1 className={`${ScoreText} m-0`}>
                     {summary?.your_avg_score}
                  </h1>
                  <div className="d-flex align-items-center flex-column">
                     {summary && (
                        <StarIcon
                           filled={
                              isNaN(productReviews?.summary?.your_avg_score / 5)
                                 ? 0
                                 : productReviews?.summary?.your_avg_score / 5
                           }
                        ></StarIcon>
                     )}
                     <span className={`${GradeText} mt-1`}>
                        {t('based on')} {summary?.your_review_count ?? 0}{' '}
                        {t('pcs grades')}
                     </span>
                  </div>
               </ReviewsStats>
               <div className="col-5 col-md-4 col-lg-5 col-xl-3">
                  <ProgressBarContainer>
                     {summary?.your_score_dist
                        ?.slice()
                        ?.reverse()
                        ?.map((distScore, i) => {
                           return (
                              <div
                                 key={i}
                                 className="d-flex gap-2 align-items-center"
                              >
                                 <span>
                                    {summary?.your_score_dist?.length - i}
                                 </span>
                                 <div className="progress">
                                    <div
                                       className="progress-bar"
                                       style={{
                                          width: `${
                                             (distScore /
                                                summary?.your_review_count) *
                                             100
                                          }%`,
                                       }}
                                       role="progressbar"
                                       aria-valuenow="0"
                                       aria-valuemin="0"
                                       aria-valuemax="100"
                                    ></div>
                                 </div>
                              </div>
                           )
                        })}
                  </ProgressBarContainer>
               </div>
            </div>
            {reviews && (
               <>
                  <hr className="my-32"></hr>
                  <ReviewsContainer className="container-fluid">
                     {reviews?.map((review, i) => {
                        return <Review key={i} review={review}></Review>
                     })}
                     <SiteButton
                        style={{ padding: '0.8rem 0.8rem 0.8rem 1.5rem' }}
                        onClick={() => loadMoreReviews()}
                        disabled={!hasNextPage}
                        styleType="solid"
                        color="blue"
                        size="medium"
                        icon={true}
                     >
                        {t('load more reviews')}
                        <NavArrowRight
                           strokeWidth={2.0}
                           style={{ marginLeft: '1rem' }}
                        ></NavArrowRight>
                     </SiteButton>
                  </ReviewsContainer>
               </>
            )}
         </ProductAccordion>
      </section>
   )
}

const Review = ({ review }) => {
   const [thumbsUpCount, setThumbsUpCount] = useState(review?.votes_up)
   const [thumbsDownCount, setThumbsDownCount] = useState(review?.votes_down)
   const t = useIntl()

   const reviewImagesArr = review?.images ?? []

   useEffect(() => {
      const data = getTestFreakData()
      if (data?.thumbsUpProductIds?.includes(getReviewId())) {
         setThumbsUpCount(thumbsUpCount + 1)
      }
      if (data?.thumbsDownProductIds?.includes(getReviewId())) {
         setThumbsDownCount(thumbsDownCount + 1)
      }
      //eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   const getReviewId = useCallback(() => {
      const parts = review?.feedback_url.split('/')
      return parts[parts.length - 2]
   }, [review?.feedback_url])

   const getTestFreakData = () => {
      const storedData = localStorage.getItem('TestFreaks')
      if (storedData) {
         return JSON.parse(storedData)
      } else {
         return { thumbsUpProductIds: [], thumbsDownProductIds: [], Report: '' }
      }
   }

   const saveTestFreakData = data => {
      localStorage.setItem('TestFreaks', JSON.stringify(data))
   }

   async function SendRequest(url) {
      try {
         const response = await fetch(url)
         if (!response.ok) {
            throw new Error('Request failed')
         }
      } catch (error) {
         // Handle any errors
         console.error(error)
      }
   }

   const updateReview = async type => {
      switch (type) {
         case 'thumbsUp':
            await SendRequest(`${review?.feedback_url}?type=up`)
            break
         case 'thumbsDown':
            await SendRequest(`${review?.feedback_url}?type=down`)
            break
         case 'reset':
            await SendRequest(`${review?.feedback_url}?type=reset`)
            break
         default:
            break
      }
   }

   const OnThumbsUpClick = async () => {
      const reviewId = getReviewId()
      let data = getTestFreakData()

      if (!data?.thumbsUpProductIds?.includes(reviewId)) {
         data?.thumbsUpProductIds?.push(reviewId)
         setThumbsUpCount(thumbsUpCount + 1)
         await updateReview('thumbsUp')
      } else {
         data.thumbsUpProductIds = data?.thumbsUpProductIds.filter(
            id => id !== reviewId
         )
         setThumbsUpCount(thumbsUpCount - 1)
         await updateReview('reset')
      }
      saveTestFreakData(data)
   }

   const OnThumbsDownClick = async () => {
      const reviewId = getReviewId()
      let data = getTestFreakData()

      if (!data?.thumbsDownProductIds?.includes(reviewId)) {
         data?.thumbsDownProductIds?.push(reviewId)
         setThumbsDownCount(thumbsDownCount + 1)
         await updateReview('thumbsDown')
      } else {
         data.thumbsDownProductIds = data?.thumbsDownProductIds.filter(
            id => id !== reviewId
         )
         setThumbsDownCount(thumbsDownCount - 1)
         await updateReview('reset')
      }
      saveTestFreakData(data)
   }

   return (
      <div className="row">
         <div className="col-1 col-lg-2 col-xxl-1 p-0 m-0">
            <div className="user-avatar">
               {review?.author.charAt(0)}
               <div className="user-verified">
                  <svg
                     width="24px"
                     height="24px"
                     strokeWidth="2.5"
                     viewBox="0 0 24 24"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                     color="#ffffff"
                  >
                     <path
                        d="M5 13l4 4L19 7"
                        stroke="#ffffff"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                     ></path>
                  </svg>
               </div>
            </div>
         </div>
         <div className="col-11 col-lg-10 col-xxl-11">
            <div className="ml-32">
               <div className="review-heading d-flex flex-wrap justify-content-between">
                  <div className="review-title mb-8">
                     <p className="mb-0">
                        {review?.author} •{' '}
                        {calculateDaysSinceDate(review?.date)} {t('days ago')} •{' '}
                        {review?.verified_buyer
                           ? t('verified buyer')
                           : t('not verified')}
                     </p>
                  </div>
                  <div className="review-actions mb-8">
                     <button onClick={OnThumbsUpClick}>
                        <ThumbsUp></ThumbsUp> {thumbsUpCount}
                     </button>
                     <button onClick={OnThumbsDownClick}>
                        <ThumbsDown></ThumbsDown> {thumbsDownCount}
                     </button>
                     {/* <button className="ml-8">
                        <Prohibition></Prohibition>
                     </button> */}
                  </div>
               </div>
               <div className="review-stars mb-16">
                  <StarIcon
                     filled={review?.score / review?.score_max}
                  ></StarIcon>
               </div>
               <div className="review-content">
                  {review?.extract ?? <br></br>}
               </div>
               {reviewImagesArr && reviewImagesArr.length > 0 && (
                  <div className="review-img-gallery d-flex gap-2 mt-16 flex-wrap">
                     {reviewImagesArr.map((imgItem, index) => (
                        <a
                        rel="noreferrer"
                           key={index}
                           className="review-img-wrapper align-content-center"
                           href={imgItem?.medium}
                           // If imgItem?.medium is falsy unset pointer-events and target
                           target={imgItem?.medium ? '_blank' : undefined}
                           style={{
                              pointerEvents: imgItem?.medium ? 'auto' : 'none',
                           }}
                        >
                           <img
                              src={imgItem?.icon}
                              alt={`Review ${index}`}
                              className="review-img-gallery-item"
                           />
                        </a>
                     ))}
                  </div>
               )}
               <hr style={{opacity: '0.15'}}className="my-32"></hr>
            </div>
         </div>
      </div>
   )
}

export default ProductReviews
